import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import {AntibodyFormat, DistinctChain, DistinctDomainKeyWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {
  chainColor,
  domainColor,
  distinctDomainToFieldName,
  distinctDomainToKey,
  formatToSortedDistinctDomainsWithChainIndex,
} from 'antibodies/AntibodyUtils';
import {
  ANTIBODY_COMPONENT_TYPE_TO_INTERFACE_ID,
  AntibodyChainSchemaFieldSpec,
  AntibodyDomainSchemaFieldSpec,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import FieldTableHeader from 'registry/field/FieldTableHeader';
import SelectSearchableDropdown from 'ui-library/dropdowns/SelectSearchableDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import type {
  AntibodyConfigurationTableQuery,
  AntibodyConfigurationTableQuery_tagSchemas_TagSchemaNode_interfaces_SchemaInterfaceNode_StaticSchemaInterfaceNode as SchemaInterfaceNode,
} from './__generated/AntibodyConfigurationTableQuery';

type TagSchemaWithInterfaces = TagSchemaSummaryGraphQL & {
  interfaces: Array<SchemaInterfaceNode>;
};

export const ANY_AA_SEQUENCE_DISPLAY = 'Any AA sequence';
const ANY_AA_SEQUENCE_VALUE = ModelTypeConstants.PROTEIN;

export const ANTIBODY_CONFIGURATION_TABLE_QUERY = gql`
  query AntibodyConfigurationTableQuery @skipAudit @engComponent(name: ANTIBODIES) {
    tagSchemas {
      id
      interfaces {
        id
      }
    }
  }
`;

function ProteinSchemaOption({iconId, label}: {iconId: BenchlingIconName; label: string}): JSX.Element {
  return (
    <div>
      <BenchlingIcon className={css(styles.icon)} icon={iconId} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Renders the domain type and specificity, with a swatch corresponding to the color for that domain in the
 * antibody viz.
 * Exported for testing.
 * @package
 */
export function DomainTypeAndSpecificityCell({
  distinctDomain,
}: {
  distinctDomain: DistinctDomainKeyWithChainIndex;
}): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-cell', css(styles.tableCell))}>
      <div className={css(styles.swatch)} style={{backgroundColor: domainColor({distinctDomain}).fill}} />
      <span>{distinctDomainToFieldName(distinctDomain)}</span>
    </div>
  );
}

/**
 * Renders the chain type and specificity, with a circle corresponding to the color for that chain in the
 * antibody viz.
 * Exported for testing.
 * @package
 */
export function ChainTypeAndSpecificityCell({
  distinctChain,
  chainIndex,
}: {
  distinctChain: DistinctChain;
  chainIndex: number;
}): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-cell', css(styles.tableCell))}>
      <div className={css(styles.dot)} style={{backgroundColor: chainColor(chainIndex)}} />
      <span>{distinctChain.label}</span>
    </div>
  );
}

export interface SharedAntibodyConfigurationTableProps {
  /* The antibody format for which an antibody schema is being configured */
  format: AntibodyFormat;
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaSummaryGraphQL>;
  disabled: boolean;
  onChangeDomainSchema: (
    domainType: string,
    specificity: string | null,
    schemaOrInterfaceId: string | null
  ) => void;
  onChangeChainSchema: (label: string, schemaOrInterfaceId: string | null) => void;
  domainSchemaFieldSpecs: Array<AntibodyDomainSchemaFieldSpec>;
  chainSchemaFieldSpecs: Array<AntibodyChainSchemaFieldSpec>;
}

export interface AntibodyConfigurationTableProps extends SharedAntibodyConfigurationTableProps {
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaWithInterfaces>;
}

export function AntibodyConfigurationTable({
  format,
  allSchemas,
  disabled,
  onChangeDomainSchema,
  onChangeChainSchema,
  domainSchemaFieldSpecs,
  chainSchemaFieldSpecs,
}: AntibodyConfigurationTableProps): JSX.Element {
  const sortedDistinctChains = useMemo(() => format.distinctChains, [format]);
  const sortedDistinctDomains = useMemo(() => formatToSortedDistinctDomainsWithChainIndex(format), [format]);
  const linkedSchemasByDomain = useMemo(
    () =>
      domainSchemaFieldSpecs.reduce(
        (acc, spec) => {
          acc[distinctDomainToKey({type: spec.domainType, specificity: spec.specificity})] =
            spec.schemaOrInterfaceId;
          return acc;
        },
        {} as Record<string, string | null>
      ),
    [domainSchemaFieldSpecs]
  );
  const linkedSchemasByChain = useMemo(
    () =>
      chainSchemaFieldSpecs.reduce(
        (acc, spec) => {
          acc[spec.label] = spec.schemaOrInterfaceId;
          return acc;
        },
        {} as Record<string, string | null>
      ),
    [chainSchemaFieldSpecs]
  );

  // @ts-expect-error TS does not believe that TagSchemaSupportedFolderItemType overlaps with
  // ModelTypeConstants despite the former being comprised entirely of members of the latter
  const proteinSchemas = allSchemas.filter((schema) => schema.folderItemType === ModelTypeConstants.PROTEIN);
  const proteinSchemasById = proteinSchemas.reduce<Record<string, TagSchemaWithInterfaces>>(
    (acc, proteinSchema) => {
      acc[proteinSchema.id] = proteinSchema;
      return acc;
    },
    {}
  );
  const optionDatas: Array<OptionData> = [
    {icon: 'protein', name: ANY_AA_SEQUENCE_DISPLAY, value: ANY_AA_SEQUENCE_VALUE},
    ...proteinSchemas.map(({iconId, id, name}) => ({
      icon: (iconId ?? 'protein') as BenchlingIconName,
      name,
      value: id,
    })),
  ];

  /**
   * Extracts the newly selected schema value from a dropdown selection, returning null if the selection hasn't changed.
   * @param key - The lookup key for the component (domain key or chain label)
   * @param values - The array of selected option values from the dropdown, should always be a single value
   * @param linkedSchemas - Map of component keys to their currently linked schema IDs
   * @returns The newly selected value, or null if the selection hasn't changed
   */
  function getSelectedValue(
    key: string,
    values: Array<string>,
    linkedSchemas: Record<string, string | null>
  ): string | null {
    const currentValue = linkedSchemas[key] ?? ANY_AA_SEQUENCE_VALUE;
    const newValues = values.filter((v) => v !== currentValue);
    if (newValues.length === 0) {
      // Clicked on the already selected value, so make no changes
      return null;
    }
    assert(newValues.length === 1, 'Attempted to select more than one value on a single value field');
    return newValues[0];
  }

  function onSelectDomainSchema(
    domainType: AntibodyDomainType,
    specificity: string | null,
    values: Array<string>
  ): void {
    const newValue = getSelectedValue(
      distinctDomainToKey({type: domainType, specificity}),
      values,
      linkedSchemasByDomain
    );
    if (newValue != null) {
      onChangeDomainSchema(domainType, specificity, newValue === ANY_AA_SEQUENCE_VALUE ? null : newValue);
    }
  }

  function onSelectChainSchema(label: string, values: Array<string>): void {
    const newValue = getSelectedValue(label, values, linkedSchemasByChain);
    if (newValue != null) {
      onChangeChainSchema(label, newValue === ANY_AA_SEQUENCE_VALUE ? null : newValue);
    }
  }

  function renderProteinSchemaDropdown({
    disabled,
    currentValue,
    onSelect,
    isSchemaValid,
    shouldAllowAnyAASequenceSchema,
  }: {
    disabled: boolean;
    currentValue: string;
    onSelect: (values: Array<string>) => void;
    isSchemaValid: (schema: TagSchemaWithInterfaces) => boolean;
    shouldAllowAnyAASequenceSchema: boolean;
  }): JSX.Element {
    return (
      <SelectSearchableDropdown
        disabled={disabled}
        menuProps={{isMulti: false}}
        noneSelectedMessage={<ProteinSchemaOption iconId="protein" label="Any AA Sequence" />}
        onSelect={onSelect}
        optionDatas={optionDatas.filter((option) =>
          option.value === ANY_AA_SEQUENCE_VALUE
            ? shouldAllowAnyAASequenceSchema
            : isSchemaValid(proteinSchemasById[option.value])
        )}
        renderSelectedValues={([schemaId]) => {
          const proteinSchema = proteinSchemasById[schemaId] ?? {
            iconId: 'protein',
            name: ANY_AA_SEQUENCE_DISPLAY,
          };
          return (
            <ProteinSchemaOption
              iconId={(proteinSchema.iconId ?? 'protein') as BenchlingIconName}
              label={proteinSchema.name}
            />
          );
        }}
        selectedValues={[currentValue]}
        closeOnInsideClick={true}
      />
    );
  }

  return (
    <div className={classNames('simpleDivTable', css(styles.table))}>
      <FieldTableHeader
        isDisabled={true}
        onAddField={null}
        subheader={<>Definition</>}
        title={BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_CHAINS ? 'Format fields' : 'Format domain field'}
      />
      {BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_CHAINS &&
        sortedDistinctChains.map((distinctChain, chainIndex) => {
          const {label} = distinctChain;
          return (
            <div key={label} className={classNames('simpleDivTable-row', css(styles.tableRow))}>
              <ChainTypeAndSpecificityCell distinctChain={distinctChain} chainIndex={chainIndex} />
              <div className="simpleDivTable-cell">
                {renderProteinSchemaDropdown({
                  disabled: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL || disabled,
                  currentValue: linkedSchemasByChain[label] ?? ANY_AA_SEQUENCE_VALUE,
                  onSelect: (values) => {
                    onSelectChainSchema(label, values);
                  },
                  isSchemaValid: (schema) =>
                    !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL ||
                    schema.interfaces
                      .map((schemaInterface) => schemaInterface.id)
                      .includes(ANTIBODY_COMPONENT_TYPE_TO_INTERFACE_ID[AntibodyComponentType.AA_CHAIN]),
                  shouldAllowAnyAASequenceSchema: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL,
                })}
              </div>
            </div>
          );
        })}
      {sortedDistinctDomains.map((distinctDomain) => {
        const {type, specificity} = distinctDomain;
        const key = distinctDomainToFieldName(distinctDomain);
        return (
          <div key={key} className={classNames('simpleDivTable-row', css(styles.tableRow))}>
            <DomainTypeAndSpecificityCell distinctDomain={distinctDomain} />
            <div className="simpleDivTable-cell">
              {renderProteinSchemaDropdown({
                disabled: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL || disabled,
                currentValue:
                  linkedSchemasByDomain[distinctDomainToKey(distinctDomain)] ?? ANY_AA_SEQUENCE_VALUE,
                onSelect: (values) => {
                  onSelectDomainSchema(type, specificity, values);
                },
                isSchemaValid: (schema) =>
                  !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL ||
                  schema.interfaces
                    .map((schemaInterface) => schemaInterface.id)
                    .includes(ANTIBODY_COMPONENT_TYPE_TO_INTERFACE_ID[AntibodyComponentType.AA_DOMAIN]),
                shouldAllowAnyAASequenceSchema: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENFORCED_DATA_MODEL,
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface QueryingAntibodyConfigurationTableProps extends SharedAntibodyConfigurationTableProps {}

export default function QueryingAntibodyConfigurationTable({
  format,
  allSchemas,
  disabled,
  onChangeDomainSchema,
  onChangeChainSchema,
  domainSchemaFieldSpecs,
  chainSchemaFieldSpecs,
}: QueryingAntibodyConfigurationTableProps): JSX.Element {
  return (
    <BenchlingQuery
      query={ANTIBODY_CONFIGURATION_TABLE_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data: AntibodyConfigurationTableQuery) => {
        const schemasById = allSchemas.reduce(
          (acc, schema) => {
            acc[schema.id] = schema;
            return acc;
          },
          {} as Record<string, TagSchemaSummaryGraphQL>
        );
        return (
          <AntibodyConfigurationTable
            format={format}
            allSchemas={data.tagSchemas.map((tagSchema) => ({
              ...schemasById[tagSchema.id],
              interfaces: tagSchema.interfaces,
            }))}
            disabled={disabled}
            onChangeDomainSchema={onChangeDomainSchema}
            onChangeChainSchema={onChangeChainSchema}
            domainSchemaFieldSpecs={domainSchemaFieldSpecs}
            chainSchemaFieldSpecs={chainSchemaFieldSpecs}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  table: {
    marginTop: '24px',
  },
  tableRow: {
    backgroundColor: Color.WHITE,
  },
  tableCell: {
    display: 'flex',
  },
  swatch: {
    width: '4px',
    minWidth: '4px',
    marginRight: '4px',
    flex: '0 0 4px',
  },
  dot: {
    width: '12px',
    minWidth: '12px',
    marginRight: '4px',
    flex: '0 0 12px',
    borderRadius: '50%',
    alignSelf: 'center',
    height: '12px',
    minHeight: '12px',
  },
  icon: {
    marginRight: '4px',
  },
});
